/**
 * RoofTemplatePick renderer · iter303ai-fix8
 *
 * Wraps the existing `RoofDiagramPickerInline` template selector so
 * operators can pick or trace a roof template inside the Workflow Runner
 * (Appraisal Booked · Schedule Appraisal task).
 *
 * The picker writes to `property.roof_template_id` under the hood. The
 * Continue button is gated until that field is populated. A "Trace from
 * this screenshot →" shortcut launches the Roof Templates page, where the
 * operator can trace + accept a fresh template that auto-links back to
 * this property.
 *
 * capturedData.property_id must be set.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layers, ArrowRight } from 'lucide-react';
import RoofDiagramPickerInline from '@/components/RoofDiagramPickerInline';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function RoofTemplatePick({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const propertyId = capturedData?.property_id;
  const navigate = useNavigate();
  const location = useLocation();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      setProp(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  // iter303ai-fix12 · Auto-reload when the route KEY changes — this fires
  // when the operator returns from /roof-templates via a navigate() call,
  // so Action 5 immediately reflects the newly-accepted template without
  // a manual page refresh.
  useEffect(() => { load(); }, [location.key, load]);

  // Also listen to visibilitychange in case the template accept happens
  // in a different tab (rare, but cheap to guard against).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const currentTemplateId = prop?.roof_template_id || '';
  const hasTemplate = !!currentTemplateId;
  const currentScreenshotId = prop?.roof_screenshot_photo_id || prop?.roof_diagram_photo_id || '';

  if (!propertyId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No property linked to this run — cannot pick a roof template.
        {canBack && (
          <button onClick={onBack} className="ml-3 text-rose-800 underline">← Back</button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="wf-roof-template-pick">
      <div className="bg-gradient-to-br from-indigo-50 to-white border-l-4 border-indigo-400 rounded-r-lg p-6 mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-indigo-600 font-semibold mb-2">
          <Layers size={12} /> Roof Template
        </div>
        <div className="text-lg text-slate-800 leading-relaxed mb-2">
          {params.prompt || "Pick the roof template that matches this property, or trace a new one from the screenshot."}
        </div>
        {currentScreenshotId && (
          <button
            type="button"
            onClick={() => {
              const qs = new URLSearchParams({
                property_id: propertyId,
                screenshot_photo_id: currentScreenshotId,
                trace: '1',
              });
              // iter303ai-fix12 · Return-to-runner handoff so Accept lands
              // straight back on this Action instead of the Property page.
              if (typeof window !== 'undefined' && window.location.pathname.startsWith('/workflow')) {
                qs.set('return_to', window.location.pathname + window.location.search);
              }
              navigate(`/roof-templates?${qs.toString()}`);
            }}
            data-testid="wf-trace-from-screenshot"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700 hover:text-indigo-900 underline decoration-dotted">
            ✎ Trace from this screenshot <ArrowRight size={13} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic mb-6">Loading property…</div>
      ) : (
        <div className="mb-6">
          <RoofDiagramPickerInline
            api={api}
            propertyId={propertyId}
            onChanged={load}
          />
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={onNext}
          disabled={!hasTemplate}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">
          {hasTemplate ? 'Accept & Continue →' : 'Pick or trace a template first'}
        </button>
      </div>
    </div>
  );
}
